import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { FormsModule } from '@angular/forms';
import { ViewInvoiceModalComponent } from './view-invoice-modal.component';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { CreateInvoiceModalComponent } from './create-invoice-modal.component';
import { InvoiceTransactionsModalComponent } from './invoice-transactions-modal.component';
import { ToastService } from '../toast.service';

interface InvoiceItem {
  product_id?: string;
  name: string;
  qty: number;
  price: number;
}

interface Invoice {
  id: string;
  invoice_id: string;
  invoice_no: string;
  invoice_number?: string;
  order_date: string;
  due_date: string;
  amount: string | number;
  amount_paid: string | number;
  items: InvoiceItem[];
  payment_type: string;
  payment_status: 'paid' | 'pending' | 'failed' | 'in_progress';
  invoice_url?: string;
  img?: string;
  is_consent: boolean;
  terms_and_conditions_accepted: boolean;
  created_at: string;
  retailer?: {
    id: string;
    retailer_name: string;
    retailer_code: string;
    email: string;
    phone_number: string;
  };
  distributor?: {
    id: string;
    distributor_name: string;
    distributor_code: string;
    email: string;
    phone_number: string;
  };
  retailer_name?: string;
  distributor_name?: string;
  issue_date?: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface InvoiceSummary {
  total: number;
  paid: number;
  overdue: number;
  totalValue: number;
}

@Component({
  selector: 'app-invoices',
  standalone: true,
  imports: [CommonModule, FormsModule, SidebarComponent, ViewInvoiceModalComponent, CreateInvoiceModalComponent, InvoiceTransactionsModalComponent],
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss']
})
export class InvoicesComponent implements OnInit {
  @ViewChild(CreateInvoiceModalComponent) createModal?: CreateInvoiceModalComponent;

  invoices: Invoice[] = [];
  pagination: Pagination = {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
    hasNext: false,
    hasPrev: false
  };

  // Overall summary (not page-scoped)
  summary: InvoiceSummary = { total: 0, paid: 0, overdue: 0, totalValue: 0 };

  searchTerm = '';
  loading = false;
  showViewModal = false;
  showCreateModal = false;
  showTransactionsModal = false;
  selectedInvoice: Invoice | null = null;
  selectedInvoiceForTransactions: Invoice | null = null;
  isEditMode = false;
  editingInvoiceId: string | null = null;
  errorMsg = '';
  userType = '';

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    private router: Router
  ) { }

  ngOnInit() {
    this.loadUserType();
    this.fetchInvoicesSummary();
    this.fetchInvoices();
  }

  loadUserType() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        this.userType = user.user_type?.toLowerCase() || '';
      } catch { }
    }
  }

  fetchInvoicesSummary() {
    this.http.get<any>(`${environment.apiBaseUrl}/invoice/summary`).subscribe({
      next: (res) => {
        if (res.statusCode === 200 && res.data) {
          this.summary = res.data;
        }
      },
      error: (err) => {
        console.error('Failed to fetch invoice summary:', err);
      }
    });
  }

  fetchInvoices(page: number = 1) {
    this.loading = true;
    this.errorMsg = '';

    const baseUrl = `${environment.apiBaseUrl}/invoice`;

    let url = `${baseUrl}?page=${page}&limit=${this.pagination.limit}`;
    if (this.searchTerm && this.searchTerm.trim().length > 0) {
      url += `&search=${encodeURIComponent(this.searchTerm.trim())}`;
    }

    this.http.get<any>(url).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.statusCode === 200 && res.data) {
          // Map API response to component format
          this.invoices = res.data.invoices.map((inv: any) => ({
            ...inv,
            invoice_number: inv.invoice_id,
            retailer_name: inv.retailer?.retailer_name || '',
            distributor_name: inv.distributor?.distributor_name || '',
            issue_date: inv.created_at,
            status: inv.payment_status
          }));
          const pg = res.data.pagination;
          this.pagination = {
            ...pg,
            hasNext: pg.page < pg.totalPages,
            hasPrev: pg.page > 1
          };
          // Scroll to top of page on page change
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          this.errorMsg = res.message || 'Failed to fetch invoices';
          this.toastService.error(res.message || 'Failed to fetch invoices');
          this.invoices = [];
        }
      },
      error: (err) => {
        this.loading = false;
        this.errorMsg = err?.error?.message || 'Failed to fetch invoices';
        this.toastService.error(err?.error?.message || 'Failed to fetch invoices');
        this.invoices = [];
      }
    });
  }

  onSearch(term: string) {
    this.searchTerm = term;
    this.fetchInvoices(1);
  }

  goToPage(page: number) {
    if (page < 1 || page > this.pagination.totalPages) return;
    this.fetchInvoices(page);
  }

  viewInvoice(invoice: Invoice) {
    this.selectedInvoice = invoice;
    this.showViewModal = true;
  }

  editInvoice(invoice: Invoice) {
    this.selectedInvoice = invoice;
    this.editingInvoiceId = invoice.id;
    this.isEditMode = true;
    this.showCreateModal = true;
  }

  goToTransactions(invoice: Invoice) {
    this.selectedInvoiceForTransactions = invoice;
    this.showTransactionsModal = true;
  }

  closeTransactionsModal() {
    this.showTransactionsModal = false;
    this.selectedInvoiceForTransactions = null;
  }

  downloadInvoice(invoice: Invoice) {
    this.http
      .get<any>(
        `${environment.apiBaseUrl}/invoice/download/${invoice.id}`,
      )
      .subscribe({
        next: (res) => {
          if (res.url) {
            fetch(res.url)
              .then(res => res.blob())
              .then(blob => {
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                let fileType = invoice.invoice_url?.split('.').pop();
                link.download = `${invoice.invoice_no}.${fileType}`;
                link.click();
              });
          } else {
            this.toastService.error('Download URL not received');
          }
        },
        error: (err) => {
          this.toastService.error(
            err?.error?.message || 'Failed to download invoice'
          );
        }
      });
  }

  downloadFile(url: string) {
    console.log("Blob url response")
    return this.http.get(url, {
      responseType: 'blob'
    });
  }

  deleteInvoice(invoice: Invoice) {
    if (invoice.payment_status !== 'pending') {
      this.toastService.error("Only pending invoices can be deleted.");
      return;
    }

    if (!confirm(`Are you sure you want to delete invoice${invoice.invoice_number ? ' ' + invoice.invoice_number : ''}?`)) {
      return;
    }

    this.http.delete<any>(
      `${environment.apiBaseUrl}/invoice/${invoice.id}`
    ).subscribe({
      next: (res) => {
        this.toastService.success(res.message);
        this.fetchInvoicesSummary();
        this.fetchInvoices(this.pagination.page);
      },
      error: (err) => {
        console.error("Delete failed:", err);
        const errorMessage = err?.error?.message || "Failed to delete invoice";
        this.toastService.error(errorMessage);
      }
    });
  }

  closeViewModal() {
    this.showViewModal = false;
    this.selectedInvoice = null;
  }

  openCreateModal() {
    // Only distributors can create invoices
    if (this.userType === 'distributor') {
      this.showCreateModal = true;
    }
  }

  closeCreateModal() {
    this.showCreateModal = false;
    this.isEditMode = false;
    this.selectedInvoice = null;
    this.editingInvoiceId = null;
  }

  handleCreateInvoice(payload: any) {
    if (payload.mode === 'create' || payload.mode === 'edit') {
      const url = payload.mode === 'edit'
        ? `${environment.apiBaseUrl}/invoice/${this.editingInvoiceId}`
        : `${environment.apiBaseUrl}/invoice/create`;

      const method = payload.mode === 'edit' ? 'put' : 'post';

      this.http[method](url, payload.data).subscribe({
        next: (res: any) => {
          if (res.statusCode === 200 || res.statusCode === 201) {
            this.toastService.success(res.message || `Invoice ${payload.mode === 'edit' ? 'updated' : 'created'} successfully!`);
            this.closeCreateModal();
            this.fetchInvoicesSummary();
            this.fetchInvoices(this.pagination.page);
          } else {
            this.toastService.error(res.message || `Failed to ${payload.mode === 'edit' ? 'update' : 'create'} invoice`);
            if (this.createModal) {
              this.createModal.resetSubmitting();
            }
          }
        },
        error: (err) => {
          this.toastService.error(err?.error?.message || `Failed to ${payload.mode === 'edit' ? 'update' : 'create'} invoice`);
          if (this.createModal) {
            this.createModal.resetSubmitting();
          }
        }
      });
    } else if (payload.mode === 'upload') {
      // For upload, use FormData and do NOT set Content-Type header (Angular will set it)
      this.http.post<any>(`${environment.apiBaseUrl}/invoice/upload`, payload.data).subscribe({
        next: (res) => {
          if (res.statusCode === 200 || res.statusCode === 201) {
            this.toastService.success(res.message || 'Invoice uploaded successfully!');
            this.closeCreateModal();
            this.fetchInvoicesSummary();
            this.fetchInvoices(1);
          } else {
            this.toastService.error(res.message || 'Failed to upload invoice');
            if (this.createModal) {
              this.createModal.resetSubmitting();
            }
          }
        },
        error: (err) => {
          this.toastService.error(err?.error?.message || 'Failed to upload invoice');
          if (this.createModal) {
            this.createModal.resetSubmitting();
          }
        }
      });
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'paid': return 'status-paid';
      case 'pending': return 'status-pending';
      case 'failed': return 'status-failed';
      case 'in_progress': return 'status-in-progress';
      default: return '';
    }
  }

  // Summary cards — sourced from overall summary, not current page
  getTotalInvoices(): number {
    return this.summary.total;
  }

  getPaidInvoices(): number {
    return this.summary.paid;
  }

  getOverdueInvoices(): number {
    return this.summary.overdue;
  }

  getTotalValue(): number {
    return this.summary.totalValue;
  }
}
